import React, { useMemo, useState } from 'react';
import { Box, Container, Grid, Typography, Dialog, IconButton, Chip } from '@mui/material';
import { Close as CloseIcon, PhotoLibrary as GalleryIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Reveal, HOME_COLORS, sectionLabelSx } from './common';

export default function GallerySection({ posts }) {
  const [lightbox, setLightbox] = useState(null); // { images: [...], index }

  const tiles = useMemo(() => {
    const all = [];
    (posts || []).forEach((post) => {
      (post.photos || []).forEach((photo) => {
        all.push({ url: `/api${photo.url}`, caption: photo.caption || post.title, postTitle: post.title });
      });
    });
    return all.slice(0, 12);
  }, [posts]);

  const openAt = (idx) => setLightbox({ images: tiles, index: idx });
  const step = (dir) => setLightbox((lb) => lb && ({ ...lb, index: (lb.index + dir + lb.images.length) % lb.images.length }));

  return (
    <Box id="gallery" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff', scrollMarginTop: '80px' }}>
      <Container maxWidth="lg">
        <Reveal>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography sx={sectionLabelSx}>Campus Life</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: HOME_COLORS.dark, fontSize: { xs: '2rem', md: '2.6rem' } }}>
              Moments from our college
            </Typography>
            <Typography sx={{ color: '#657875', maxWidth: 640, mx: 'auto', mt: 1.5 }}>
              A glimpse of campus life, events, clinical training and celebrations at our college.
            </Typography>
          </Box>
        </Reveal>

        {tiles.length === 0 ? (
          <Reveal>
            <Box sx={{ textAlign: 'center', py: 8, color: '#8a9a97' }}>
              <GalleryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
              <Typography>Gallery photos will appear here soon.</Typography>
            </Box>
          </Reveal>
        ) : (
          <Grid container spacing={2}>
            {tiles.map((t, idx) => (
              <Grid item xs={12} sm={6} md={4} key={`${t.url}-${idx}`}>
                <Reveal delay={(idx % 6) * 70}>
                  <Box
                    onClick={() => openAt(idx)}
                    sx={{
                      position: 'relative', borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
                      aspectRatio: '4/3', bgcolor: '#e3efec',
                      '&:hover img': { transform: 'scale(1.08)' },
                      '&:hover .overlay': { opacity: 1 },
                    }}
                  >
                    <Box
                      component="img"
                      src={t.url}
                      alt={t.caption}
                      loading="lazy"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }}
                    />
                    <Box
                      className="overlay"
                      sx={{
                        position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,37,26,0) 40%, rgba(0,37,26,0.85) 100%)',
                        opacity: 0, transition: 'opacity 0.3s ease', display: 'flex', alignItems: 'flex-end', p: 2,
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>{t.caption}</Typography>
                    </Box>
                  </Box>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: '#000', borderRadius: 3, overflow: 'hidden' } }}>
        {lightbox && (
          <Box sx={{ position: 'relative' }}>
            <IconButton onClick={() => setLightbox(null)} sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 2, bgcolor: 'rgba(255,255,255,0.1)' }}>
              <CloseIcon />
            </IconButton>
            <IconButton onClick={() => step(-1)} sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', color: '#fff', zIndex: 2, bgcolor: 'rgba(255,255,255,0.1)' }}>
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={() => step(1)} sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', color: '#fff', zIndex: 2, bgcolor: 'rgba(255,255,255,0.1)' }}>
              <ChevronRight />
            </IconButton>
            <Box
              component="img"
              src={lightbox.images[lightbox.index]?.url}
              alt=""
              sx={{ width: '100%', maxHeight: '78vh', objectFit: 'contain', display: 'block', bgcolor: '#000' }}
            />
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ color: '#fff' }}>{lightbox.images[lightbox.index]?.caption}</Typography>
              <Chip size="small" label={`${lightbox.index + 1} / ${lightbox.images.length}`} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
